import React, { useState, useEffect, useRef } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

// Import Highcharts modules properly
import 'highcharts/modules/exporting';
import 'highcharts/modules/export-data';
import 'highcharts/modules/accessibility';

import './Statistics.css';
import HeaderOne from '../Components/Header/HeaderOne';
import FooterOne from '../Components/Footer/FooterOne';

const Statistics = () => {
  const [stats, setStats] = useState({
    totalTowers: 0,
    totalWifi: 0,
    totalUsers: 0,
    activeTowers: 0,
    activeWifi: 0,
    towersByOperator: [],
    wifiByProvider: [],
    towersByStatus: [],
    wifiByStatus: [],
    towersByKecamatan: [],
    wifiByKecamatan: [],
    towersByJenis: [],
    recentTowers: [],
    recentWifi: []
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  const chartRefs = useRef({});

  useEffect(() => {
    // Configure Highcharts global options
    Highcharts.setOptions({
      lang: {
        contextButtonTitle: 'Opsi Ekspor',
        downloadJPEG: 'Unduh JPEG',
        downloadPDF: 'Unduh PDF',
        downloadPNG: 'Unduh PNG',
        downloadSVG: 'Unduh SVG',
        printChart: 'Cetak Grafik',
        viewFullscreen: 'Lihat Layar Penuh',
        viewData: 'Lihat Data Tabel',
        hideData: 'Sembunyikan Data',
        downloadCSV: 'Unduh CSV',
        downloadXLS: 'Unduh Excel'
      },
      chart: {
        style: {
          fontFamily: 'Inter, system-ui, sans-serif'
        },
        backgroundColor: 'transparent'
      },
      colors: ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#84cc16', '#f97316'],
      exporting: {
        enabled: true,
        fallbackToExportServer: false,
        buttons: {
          contextButton: {
            menuItems: [
              'viewFullscreen',
              'separator',
              'downloadPNG',
              'downloadJPEG',
              'downloadPDF',
              'downloadSVG',
              'separator',
              'viewData',
              'downloadCSV',
              'downloadXLS',
              'separator',
              'printChart'
            ]
          }
        },
        filename: 'Koorditel-ManggaraiBarat-Statistik-{type}-{date}',
        csv: {
          dateFormat: '%Y-%m-%d %H:%M:%S'
        },
        menuItemDefinitions: {
          viewData: {
            textKey: 'viewData',
            onclick: function() {
              this.viewData();
            }
          }
        }
      },
      credits: {
        enabled: false
      },
      title: {
        style: {
          fontSize: '18px',
          fontWeight: 'bold',
          color: '#374151'
        }
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#e5e7eb',
        borderRadius: 8,
        borderWidth: 1,
        shadow: {
          color: 'rgba(0, 0, 0, 0.1)',
          offsetX: 0,
          offsetY: 4,
          opacity: 0.1,
          width: 8
        },
        style: {
          color: '#374151',
          fontSize: '12px'
        }
      }
    });

    loadStatistics();
  }, []);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.export-dropdown')) {
        setExportDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadStatistics = async () => {
    setLoading(true);
    setError(null);

    try {
      const endpoints = [
        { url: 'https://monevmedsos.manggaraibaratkab.go.id/apiwifi.php/towers', key: 'towers' },
        { url: 'https://monevmedsos.manggaraibaratkab.go.id/apiwifi.php/wifi', key: 'wifi' },
        { url: 'https://monevmedsos.manggaraibaratkab.go.id/apiwifi.php/users', key: 'users' },
        { url: 'https://monevmedsos.manggaraibaratkab.go.id/apiwifi.php/jenis', key: 'jenis' },
        { url: 'https://monevmedsos.manggaraibaratkab.go.id/apiwifi.php/operator', key: 'operator' },
        { url: 'https://monevmedsos.manggaraibaratkab.go.id/apiwifi.php/kecamatan', key: 'kecamatan' },
        { url: 'https://monevmedsos.manggaraibaratkab.go.id/apiwifi.php/provider', key: 'provider' }
      ];

      const results = await Promise.allSettled(
        endpoints.map(endpoint =>
          fetch(endpoint.url).then(res => {
            if (!res.ok) {
              throw new Error(`HTTP ${res.status}: ${res.statusText}`);
            }
            return res.json().catch(() => []);
          })
        )
      );

      const data = {};
      results.forEach((result, index) => {
        const key = endpoints[index].key;
        if (result.status === 'fulfilled') {
          data[key] = result.value || [];
        } else {
          console.warn(`Failed to load ${key}:`, result.reason);
          data[key] = [];
        }
      });

      // Process statistics
      const totalTowers = data.towers.length;
      const totalWifi = data.wifi.length;
      const totalUsers = data.users.length;
      const activeTowers = data.towers.filter(t => t.status === 'Aktif').length;
      const activeWifi = data.wifi.filter(w => w.status === 'Aktif').length;

      const towersByOperator = data.operator.map(op => ({
        name: op.nama,
        y: data.towers.filter(t => t.operator === op.nama).length
      })).filter(item => item.y > 0);

      const wifiByProvider = data.provider.map(p => ({
        name: p.nama,
        y: data.wifi.filter(w => w.provider === p.nama).length
      })).filter(item => item.y > 0);

      const towersByStatus = [
        { name: 'Aktif', y: activeTowers, color: '#10b981' },
        { name: 'Tidak Aktif', y: totalTowers - activeTowers, color: '#ef4444' }
      ].filter(item => item.y > 0);

      const wifiByStatus = [
        { name: 'Aktif', y: activeWifi, color: '#10b981' },
        { name: 'Tidak Aktif', y: totalWifi - activeWifi, color: '#ef4444' }
      ].filter(item => item.y > 0);

      const towersByKecamatan = data.kecamatan.map(k => ({
        name: k.nama,
        y: data.towers.filter(t => t.kecamatan === k.nama).length
      })).filter(item => item.y > 0).sort((a, b) => b.y - a.y);

      const wifiByKecamatan = data.kecamatan.map(k => ({
        name: k.nama,
        y: data.wifi.filter(w => w.kecamatan === k.nama).length
      })).filter(item => item.y > 0).sort((a, b) => b.y - a.y);

      const towersByJenis = data.jenis.map(j => ({
        name: j.nama,
        y: data.towers.filter(t => t.jenis === j.nama).length
      })).filter(item => item.y > 0);

      const recentTowers = data.towers
        .filter(t => t.created_at)
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 5);

      const recentWifi = data.wifi
        .filter(w => w.created_at)
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 5);

      setStats({
        totalTowers,
        totalWifi,
        totalUsers,
        activeTowers,
        activeWifi,
        towersByOperator,
        wifiByProvider,
        towersByStatus,
        wifiByStatus,
        towersByKecamatan,
        wifiByKecamatan,
        towersByJenis,
        recentTowers,
        recentWifi
      });

      setLastUpdate(new Date());

    } catch (error) {
      console.error('Error loading statistics:', error);
      setError('Gagal memuat data statistik. Silakan coba lagi.');
    } finally {
      setLoading(false);
    }
  };

  const exportChart = (chartId, format = 'png') => {
    const chart = chartRefs.current[chartId];
    if (!chart || !chart.chart) return;

    const filename = `Koorditel-ManggaraiBarat-${chartId.replace('-', '-')}-${new Date().toISOString().split('T')[0]}`;

    switch (format) {
      case 'png':
        chart.chart.exportChart({
          type: 'image/png',
          filename: filename
        });
        break;
      case 'jpg':
        chart.chart.exportChart({
          type: 'image/jpeg',
          filename: filename
        });
        break;
      case 'pdf':
        chart.chart.exportChart({
          type: 'application/pdf',
          filename: filename
        });
        break;
      case 'svg':
        chart.chart.exportChart({
          type: 'image/svg+xml',
          filename: filename
        });
        break;
      default:
        chart.chart.exportChart({
          type: 'image/png',
          filename: filename
        });
    }
  };

  const exportAllCharts = () => {
    // Create a combined export of all charts data as CSV
    const summaryData = [
      ['Kategori', 'Nilai'],
      ['Total Menara', stats.totalTowers],
      ['Total WiFi', stats.totalWifi],
      ['Total Pengguna', stats.totalUsers],
      ['Menara Aktif', stats.activeTowers],
      ['WiFi Aktif', stats.activeWifi],
      [''],
      ['Status Menara'],
      ...stats.towersByStatus.map(item => [item.name, item.y]),
      [''],
      ['Status WiFi'],
      ...stats.wifiByStatus.map(item => [item.name, item.y]),
      [''],
      ['Menara per Operator'],
      ...stats.towersByOperator.map(item => [item.name, item.y]),
      [''],
      ['WiFi per Provider'],
      ...stats.wifiByProvider.map(item => [item.name, item.y]),
      [''],
      ['Menara per Jenis'],
      ...stats.towersByJenis.map(item => [item.name, item.y]),
      [''],
      ['Menara per Kecamatan'],
      ...stats.towersByKecamatan.map(item => [item.name, item.y]),
      [''],
      ['WiFi per Kecamatan'],
      ...stats.wifiByKecamatan.map(item => [item.name, item.y])
    ];

    // Create a temporary element to trigger download
    const element = document.createElement('a');
    const csvContent = summaryData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    element.href = url;
    element.download = `Koorditel-ManggaraiBarat-Statistik-Lengkap-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    URL.revokeObjectURL(url);
  };

  const exportAllChartsAsImages = async () => {
    const charts = Object.entries(chartRefs.current);
    if (charts.length === 0) return;

    // Create a zip-like collection of images
    for (const [chartId, chartRef] of charts) {
      if (chartRef && chartRef.chart) {
        const filename = `Koorditel-ManggaraiBarat-${chartId.replace('-', '-')}-${new Date().toISOString().split('T')[0]}`;
        chartRef.chart.exportChart({
          type: 'image/png',
          filename: filename
        });
        // Add small delay between exports
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }
  };

  const exportDashboardPDF = () => {
    // Create a comprehensive PDF report
    const printWindow = window.open('', '_blank');
    const dashboardContent = document.querySelector('.statistics-page');

    if (printWindow && dashboardContent) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Dashboard Statistik PetaWiFi</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .stats-header { text-align: center; margin-bottom: 30px; }
            .stats-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; margin-bottom: 30px; }
            .stat-card { border: 1px solid #ddd; padding: 20px; border-radius: 8px; text-align: center; }
            .chart-container { margin-bottom: 30px; }
            .recent-activity { margin-top: 30px; }
            @media print { .no-print { display: none; } }
          </style>
        </head>
        <body>
          <div class="stats-header">
            <h1>Dashboard Statistik Koorditel Kabupaten Manggarai Barat</h1>
            <p>Data real-time infrastruktur telekomunikasi dan WiFi publik</p>
            <p>Dicetak pada: ${new Date().toLocaleString('id-ID')}</p>
          </div>
          ${dashboardContent.innerHTML}
        </body>
        </html>
      `);

      printWindow.document.close();
      printWindow.print();
    }
  };

  const setChartRef = (chartId, chartRef) => {
    if (chartRef) {
      chartRefs.current[chartId] = chartRef;
    }
  };

  const StatCard = ({ title, value, subtitle, icon, color, bgColor }) => (
    <div className="stat-card" style={{ '--card-color': color, '--card-bg': bgColor }}>
      <div className="stat-icon">
        <span>{icon}</span>
      </div>
      <div className="stat-content">
        <h3>{value}</h3>
        <p className="stat-title">{title}</p>
        {subtitle && <p className="stat-subtitle">{subtitle}</p>}
      </div>
    </div>
  );

  const ChartContainer = ({ title, children, chartId }) => (
    <div className="chart-container">
      <div className="chart-header">
        <h3>{title}</h3>
        {chartId && (
          <div className="chart-export-menu">
            <button className="chart-export-btn" onClick={() => exportChart(chartId, 'png')}>
              📥 PNG
            </button>
            <button className="chart-export-btn" onClick={() => exportChart(chartId, 'pdf')}>
              📄 PDF
            </button>
            <button className="chart-export-btn" onClick={() => exportChart(chartId, 'svg')}>
              🎨 SVG
            </button>
          </div>
        )}
      </div>
      {children}
    </div>
  );

  const createPieChartOptions = (title, data, chartId) => ({
    chart: {
      type: 'pie',
      backgroundColor: 'transparent',
      height: 350,
      style: {
        fontFamily: 'Inter, system-ui, sans-serif'
      }
    },
    title: {
      text: title,
      style: {
        fontSize: '18px',
        fontWeight: 'bold',
        color: '#374151'
      }
    },
    tooltip: {
      pointFormat: '<b>{point.name}</b>: {point.y} ({point.percentage:.1f}%)',
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderColor: '#e5e7eb',
      borderRadius: 8,
      borderWidth: 1,
      shadow: {
        color: 'rgba(0, 0, 0, 0.1)',
        offsetX: 0,
        offsetY: 4,
        opacity: 0.1,
        width: 8
      }
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: 'pointer',
        dataLabels: {
          enabled: true,
          format: '<b>{point.name}</b>: {point.percentage:.1f}%',
          style: {
            fontSize: '12px',
            fontWeight: 'bold',
            color: '#374151'
          },
          distance: 20
        },
        showInLegend: true,
        borderWidth: 2,
        borderColor: '#ffffff'
      }
    },
    legend: {
      align: 'right',
      verticalAlign: 'middle',
      layout: 'vertical',
      itemStyle: {
        color: '#6b7280',
        fontSize: '12px'
      }
    },
    series: [{
      name: 'Jumlah',
      colorByPoint: true,
      data: data
    }],
    credits: { enabled: false },
    exporting: {
      enabled: true,
      filename: `PetaWiFi-${title.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}`,
      menuItemDefinitions: {
        viewData: {
          textKey: 'viewData',
          onclick: function() {
            this.viewData();
          }
        }
      }
    }
  });

  const createBarChartOptions = (title, data, chartId) => ({
    chart: {
      type: 'column',
      backgroundColor: 'transparent',
      height: 400,
      style: {
        fontFamily: 'Inter, system-ui, sans-serif'
      }
    },
    title: {
      text: title,
      style: {
        fontSize: '18px',
        fontWeight: 'bold',
        color: '#374151'
      }
    },
    xAxis: {
      categories: data.map(item => item.name),
      crosshair: true,
      labels: {
        style: {
          color: '#6b7280',
          fontSize: '11px'
        },
        rotation: data.length > 5 ? -45 : 0
      }
    },
    yAxis: {
      min: 0,
      title: {
        text: 'Jumlah',
        style: {
          color: '#6b7280'
        }
      },
      labels: {
        style: {
          color: '#6b7280'
        }
      },
      gridLineColor: '#f3f4f6'
    },
    tooltip: {
      headerFormat: '<span style="font-size:12px;color:#374151"><b>{point.key}</b></span><br/>',
      pointFormat: '<span style="color:{series.color}">●</span> {series.name}: <b>{point.y}</b><br/>',
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderColor: '#e5e7eb',
      borderRadius: 8,
      borderWidth: 1,
      shadow: {
        color: 'rgba(0, 0, 0, 0.1)',
        offsetX: 0,
        offsetY: 4,
        opacity: 0.1,
        width: 8
      }
    },
    plotOptions: {
      column: {
        pointPadding: 0.2,
        borderWidth: 0,
        borderRadius: 4,
        dataLabels: {
          enabled: true,
          format: '{point.y}',
          style: {
            fontSize: '11px',
            fontWeight: 'bold',
            color: '#374151'
          }
        },
        states: {
          hover: {
            brightness: 0.1
          }
        }
      }
    },
    series: [{
      name: 'Jumlah',
      data: data.map((item, index) => ({
        y: item.y,
        color: item.color || `hsl(${index * 45}, 70%, 50%)`
      })),
      borderRadius: 4
    }],
    credits: { enabled: false },
    exporting: {
      enabled: true,
      filename: `PetaWiFi-${title.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}`,
      menuItemDefinitions: {
        viewData: {
          textKey: 'viewData',
          onclick: function() {
            this.viewData();
          }
        }
      }
    }
  });

  const createLineChartOptions = (title, data, chartId) => ({
    chart: {
      type: 'line',
      backgroundColor: 'transparent',
      height: 400,
      style: {
        fontFamily: 'Inter, system-ui, sans-serif'
      }
    },
    title: {
      text: title,
      style: {
        fontSize: '18px',
        fontWeight: 'bold',
        color: '#374151'
      }
    },
    xAxis: {
      categories: data.map(item => item.name),
      labels: {
        style: {
          color: '#6b7280',
          fontSize: '11px'
        },
        rotation: data.length > 5 ? -45 : 0
      },
      gridLineColor: '#f3f4f6'
    },
    yAxis: {
      title: {
        text: 'Jumlah',
        style: {
          color: '#6b7280'
        }
      },
      labels: {
        style: {
          color: '#6b7280'
        }
      },
      gridLineColor: '#f3f4f6'
    },
    tooltip: {
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderColor: '#e5e7eb',
      borderRadius: 8,
      borderWidth: 1,
      shadow: {
        color: 'rgba(0, 0, 0, 0.1)',
        offsetX: 0,
        offsetY: 4,
        opacity: 0.1,
        width: 8
      },
      style: {
        color: '#374151'
      }
    },
    plotOptions: {
      line: {
        dataLabels: {
          enabled: true,
          format: '{point.y}',
          style: {
            fontSize: '10px',
            fontWeight: 'bold',
            color: '#374151'
          }
        },
        enableMouseTracking: true,
        marker: {
          radius: 4,
          fillColor: '#3b82f6',
          lineWidth: 2,
          lineColor: '#ffffff'
        },
        states: {
          hover: {
            lineWidth: 3
          }
        }
      }
    },
    series: [{
      name: 'Jumlah',
      data: data.map(item => item.y),
      color: '#3b82f6',
      lineWidth: 3,
      marker: {
        enabled: true,
        radius: 4,
        fillColor: '#3b82f6',
        lineWidth: 2,
        lineColor: '#ffffff'
      }
    }],
    credits: { enabled: false },
    exporting: {
      enabled: true,
      filename: `PetaWiFi-${title.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}`,
      menuItemDefinitions: {
        viewData: {
          textKey: 'viewData',
          onclick: function() {
            this.viewData();
          }
        }
      }
    }
  });

  if (loading) {
    return (
      <>
        <HeaderOne />
        <div className="statistics-page">
          <div className="loading-container">
            <div className="loading-spinner">
              <div className="spinner-ring"></div>
              <div className="spinner-ring"></div>
              <div className="spinner-ring"></div>
              <div className="spinner-ring"></div>
            </div>
            <h3>Memuat Dashboard Statistik</h3>
            <p>Mengambil data real-time dari infrastruktur telekomunikasi...</p>
            <div className="loading-progress">
              <div className="progress-bar"></div>
            </div>
          </div>
        </div>
        <FooterOne />
      </>
    );
  }

  if (error) {
    return (
      <>
        <HeaderOne />
        <div className="statistics-page">
          <div className="error-container">
            <div className="error-icon">⚠️</div>
            <h3>Terjadi Kesalahan</h3>
            <p>{error}</p>
            <button className="retry-btn" onClick={loadStatistics}>
              🔄 Coba Lagi
            </button>
          </div>
        </div>
        <FooterOne />
      </>
    );
  }

  return (
    <>
      <HeaderOne />
      <div className="statistics-page">
        <div className="stats-header">
          <div className="header-content">
            <div className="header-text">
              <h1>📊 Dashboard Statistik Koorditel Kabupaten Manggarai Barat</h1>
              <p>Data real-time infrastruktur telekomunikasi dan WiFi publik</p>
              <div className="last-update">
                <small>📅 Terakhir diperbarui: {lastUpdate.toLocaleString('id-ID')}</small>
              </div>
            </div>
            <div className="header-actions">
              <div className="export-dropdown">
                <button
                  className="export-dropdown-btn"
                  onClick={() => setExportDropdownOpen(!exportDropdownOpen)}
                >
                  📊 Export Data {exportDropdownOpen ? '▲' : '▼'}
                </button>
                {exportDropdownOpen && (
                  <div className="export-dropdown-menu">
                    <button onClick={() => { exportAllCharts(); setExportDropdownOpen(false); }} className="export-option">
                      📄 Export CSV Lengkap
                    </button>
                    <button onClick={() => { exportAllChartsAsImages(); setExportDropdownOpen(false); }} className="export-option">
                      🖼️ Export Semua Chart (PNG)
                    </button>
                    <button onClick={() => { exportDashboardPDF(); setExportDropdownOpen(false); }} className="export-option">
                      🖨️ Export Dashboard (Print)
                    </button>
                  </div>
                )}
              </div>
              <button className="refresh-btn" onClick={loadStatistics}>
                🔄 Refresh Data
              </button>
            </div>
          </div>
        </div>

        <section className="stats-section">
          <h2>📈 Ringkasan Umum</h2>
          <div className="stats-grid overview-grid">
            <StatCard
              title="Total Menara"
              value={stats.totalTowers}
              subtitle="Menara Telekomunikasi"
              icon="📡"
              color="#3b82f6"
              bgColor="linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)"
            />
            <StatCard
              title="Total WiFi"
              value={stats.totalWifi}
              subtitle="Lokasi WiFi Publik"
              icon="📶"
              color="#10b981"
              bgColor="linear-gradient(135deg, #10b981 0%, #059669 100%)"
            />
            <StatCard
              title="Pengguna Sistem"
              value={stats.totalUsers}
              subtitle="Total User Terdaftar"
              icon="👥"
              color="#f59e0b"
              bgColor="linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
            />
            <StatCard
              title="Lokasi Aktif"
              value={stats.activeTowers + stats.activeWifi}
              subtitle="Total Infrastruktur Aktif"
              icon="🗺️"
              color="#8b5cf6"
              bgColor="linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)"
            />
          </div>
        </section>

        <section className="stats-section">
          <h2>⚡ Status Infrastruktur</h2>
          <div className="stats-grid status-grid">
            <ChartContainer title="Status Menara Telekomunikasi" chartId="towers-status">
              <HighchartsReact
                highcharts={Highcharts}
                options={createPieChartOptions('Status Menara Telekomunikasi', stats.towersByStatus, 'towers-status')}
                ref={(ref) => setChartRef('towers-status', ref)}
              />
            </ChartContainer>
            <ChartContainer title="Status Lokasi WiFi" chartId="wifi-status">
              <HighchartsReact
                highcharts={Highcharts}
                options={createPieChartOptions('Status Lokasi WiFi', stats.wifiByStatus, 'wifi-status')}
                ref={(ref) => setChartRef('wifi-status', ref)}
              />
            </ChartContainer>
          </div>
        </section>

        <section className="stats-section">
          <h2>🏢 Distribusi Menara</h2>
          <div className="stats-grid distribution-grid">
            <ChartContainer title="Menara per Operator" chartId="towers-operator">
              <HighchartsReact
                highcharts={Highcharts}
                options={createBarChartOptions('Menara per Operator', stats.towersByOperator, 'towers-operator')}
                ref={(ref) => setChartRef('towers-operator', ref)}
              />
            </ChartContainer>
            <ChartContainer title="Menara per Jenis" chartId="towers-jenis">
              <HighchartsReact
                highcharts={Highcharts}
                options={createBarChartOptions('Menara per Jenis', stats.towersByJenis, 'towers-jenis')}
                ref={(ref) => setChartRef('towers-jenis', ref)}
              />
            </ChartContainer>
          </div>
        </section>

        <section className="stats-section">
          <h2>📶 Distribusi WiFi</h2>
          <ChartContainer title="WiFi per Provider" chartId="wifi-provider">
            <HighchartsReact
              highcharts={Highcharts}
              options={createBarChartOptions('WiFi per Provider', stats.wifiByProvider, 'wifi-provider')}
              ref={(ref) => setChartRef('wifi-provider', ref)}
            />
          </ChartContainer>
        </section>

        <section className="stats-section">
          <h2>🗺️ Distribusi Geografis</h2>
          <div className="stats-grid geo-grid">
            <ChartContainer title="Menara per Kecamatan" chartId="towers-kecamatan">
              <HighchartsReact
                highcharts={Highcharts}
                options={createLineChartOptions('Menara per Kecamatan', stats.towersByKecamatan.slice(0, 8), 'towers-kecamatan')}
                ref={(ref) => setChartRef('towers-kecamatan', ref)}
              />
            </ChartContainer>
            <ChartContainer title="WiFi per Kecamatan" chartId="wifi-kecamatan">
              <HighchartsReact
                highcharts={Highcharts}
                options={createLineChartOptions('WiFi per Kecamatan', stats.wifiByKecamatan.slice(0, 8), 'wifi-kecamatan')}
                ref={(ref) => setChartRef('wifi-kecamatan', ref)}
              />
            </ChartContainer>
          </div>
        </section>

        <section className="stats-section">
          <h2>🕒 Aktivitas Terbaru</h2>
          <div className="recent-activity">
            <div className="activity-container">
              <h3>Menara Terbaru</h3>
              <div className="activity-list">
                {stats.recentTowers.map((tower, index) => (
                  <div key={index} className="activity-item">
                    <div className="activity-icon">📡</div>
                    <div className="activity-content">
                      <h4>{tower.nama_optel}</h4>
                      <p>{tower.lokasi}, {tower.kecamatan}</p>
                      <small>{new Date(tower.created_at).toLocaleDateString('id-ID')}</small>
                    </div>
                    <div className={`activity-status ${tower.status === 'Aktif' ? 'active' : 'inactive'}`}>
                      {tower.status}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="activity-container">
              <h3>WiFi Terbaru</h3>
              <div className="activity-list">
                {stats.recentWifi.map((wifi, index) => (
                  <div key={index} className="activity-item">
                    <div className="activity-icon">📶</div>
                    <div className="activity-content">
                      <h4>{wifi.nama}</h4>
                      <p>{wifi.lokasi}, {wifi.kecamatan}</p>
                      <small>{new Date(wifi.created_at).toLocaleDateString('id-ID')}</small>
                    </div>
                    <div className={`activity-status ${wifi.status === 'Aktif' ? 'active' : 'inactive'}`}>
                      {wifi.status}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </div>
      <FooterOne />
    </>
  );
};

export default Statistics;